import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { API } from '@/App';
import Logo from '@/components/Logo';

/**
 * Native Google OAuth callback landing page.
 *
 * Flow:
 *   Capacitor Browser  →  /api/auth/google/authorize
 *   → Google consent   →  /api/auth/google/callback
 *   → 302 to           →  unityblaze://auth/native-callback?token=<jwt>
 *   → iOS/Android open the app; deepLinks.js navigates here.
 *
 * This page reads `?token=` from the URL, fetches the user with a Bearer
 * header, stores auth state via AuthContext.setAuthToken + updateUser, and
 * routes to the appropriate post-login destination.
 */
const NativeAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthToken, updateUser } = useAuth();
  const consumedRef = useRef(false);

  useEffect(() => {
    // Guard against double-invocation from React StrictMode.
    if (consumedRef.current) return;
    consumedRef.current = true;

    const run = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');

      if (error) {
        toast.error(`Google sign-in failed: ${error}`);
        navigate('/login', { replace: true });
        return;
      }
      if (!token) {
        toast.error('Missing auth token');
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Fetch the user with the freshly-minted JWT as a Bearer header —
        // native WebView doesn't share cookies with the redirect origin so
        // we can't rely on the httpOnly cookie path here.
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAuthToken(token);
        updateUser(res.data);
        toast.success(`Welcome, ${res.data.name}!`);

        if (res.data.is_admin) navigate('/admin', { replace: true });
        else if (!res.data.quiz_completed) navigate('/onboarding', { replace: true });
        else navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Native auth callback error:', err);
        toast.error('Could not complete sign-in');
        navigate('/login', { replace: true });
      }
    };

    run();
  }, [searchParams, navigate, setAuthToken, updateUser]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background"
      data-testid="native-auth-callback"
    >
      <div className="flex flex-col items-center gap-6">
        <Logo size="large" />
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

export default NativeAuthCallback;
